import {Dispatch, MutableRefObject, SetStateAction, useCallback, useEffect, useRef} from 'react';
import {OverlayVideo} from '~/QuickEcards/components/OverlayAnimation/OverlayAnimation';

type CardAnimationDetails = {
  revealFinishedRef: MutableRefObject<boolean>;
  effectFinishedRef: MutableRefObject<boolean>;
  playFullAnimation: () => Promise<void>;
  replayAnimation: () => void;
};

interface UseCardAnimationProps {
  hasReveal: boolean;
  hasEffect: boolean;
  message: string | null;
  revealVideo: OverlayVideo | null;
  effectVideo: OverlayVideo | null;
  setAnimation: Dispatch<SetStateAction<string>>;
  setAnimationFinished: Dispatch<SetStateAction<boolean>>;
  disableFlip?: boolean;
}

export const useCardAnimation = ({
  hasReveal,
  hasEffect,
  message,
  revealVideo,
  effectVideo,
  setAnimation,
  setAnimationFinished,
  disableFlip = false,
}: UseCardAnimationProps): CardAnimationDetails => {
  const revealFinishedRef = useRef<boolean>(false);
  const effectFinishedRef = useRef<boolean>(false);
  const messageRef = useRef<string | null>(message);
  // biome-ignore lint/suspicious/noEmptyBlockStatements: existing error
  const {play: playReveal = () => {}} = revealVideo ?? {play: () => {}};
  // biome-ignore lint/suspicious/noEmptyBlockStatements: existing error
  const {play: playEffect = () => {}} = effectVideo ?? {play: () => {}};

  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  // Listen for onEnded callback from OverlayAnimation
  const waitForRevealOrEffect = (animRef: MutableRefObject<boolean>): Promise<void> =>
    new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (animRef.current) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });

  // If hasReveal, play reveal animation
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const handlePlayReveal = useCallback(async () => {
    if (hasReveal) {
      setTimeout(() => {
        setAnimation('reveal');
      }, 50);
      playReveal();
      await waitForRevealOrEffect(revealFinishedRef);
    }
  }, [hasReveal, playReveal, setAnimation]);

  // If !hasReveal, play card animation
  const playCardAnimation = useCallback(
    (): Promise<void> =>
      new Promise<void>((resolve) => {
        if (hasReveal) {
          resolve();
        } else {
          setTimeout(() => {
            setAnimation('slide-up');
            setTimeout(() => {
              resolve();
            }, 800);
          }, 500);
        }
      }),
    [hasReveal, setAnimation]
  );

  // If hasMessage, flip card over
  const flipCard = useCallback(
    (): Promise<void> =>
      new Promise((resolve) => {
        if (messageRef.current) {
          setAnimation('flip');
          setTimeout(() => {
            resolve();
          }, 800);
        } else {
          resolve();
        }
      }),
    [setAnimation]
  );

  // If hasEffect, play effect animation
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const handlePlayEffect = useCallback(async () => {
    if (hasEffect) {
      playEffect();
      await waitForRevealOrEffect(effectFinishedRef);
    }
  }, [hasEffect, playEffect]);

  // Full animation sequence
  const playFullAnimation = useCallback(async () => {
    await handlePlayReveal();
    await playCardAnimation();
    if (!disableFlip) await flipCard();
    await handlePlayEffect();
    setAnimationFinished(true);
  }, [
    handlePlayReveal,
    playCardAnimation,
    disableFlip,
    flipCard,
    handlePlayEffect,
    setAnimationFinished,
  ]);

  // Reset and replay full animation
  const replayAnimation = useCallback(() => {
    revealFinishedRef.current = false;
    effectFinishedRef.current = false;
    setAnimationFinished(false);
    setAnimation('');
    playFullAnimation();
  }, [playFullAnimation, setAnimation, setAnimationFinished]);

  const watchEffectProgress = useCallback(
    (e: Event) => {
      const video = e.target as HTMLVideoElement;
      if (video.currentTime / video.duration >= 0.8) {
        setAnimationFinished(true);
      }
    },
    [setAnimationFinished]
  );

  useEffect(() => {
    if (!hasEffect) return undefined;
    effectVideo?.videoRef?.current?.addEventListener('timeupdate', watchEffectProgress);
    return () => {
      effectVideo?.videoRef?.current?.removeEventListener('timeupdate', watchEffectProgress);
    };
  }, [hasEffect, watchEffectProgress, effectVideo?.videoRef?.current]);

  return {
    revealFinishedRef,
    effectFinishedRef,
    playFullAnimation,
    replayAnimation,
  };
};
